/**
 * Service for reading server variables injected as meta tags in the HTML head.
 *
 * In staging/production, Django renders the index.html template and replaces
 * the {{region.host}}, {{region.id}}, and {{deal_id}} placeholders before serving.
 *
 * In preview deployments, index.html is served statically from S3 via nginx,
 * bypassing Django entirely. The meta tags remain unreplaced, so we fall back
 * to parsing dealId and regionId from the URL — from the path
 * (/apps/deal/{regionId}/{dealId} for React, /apps/dealroom/{regionId}/{dealId} for Ember)
 * or from page/hash query params for static preview builds.
 * regionHost is handled separately via the token-based fallback in auth.ts.
 */
class ServerVariablesService {
  private cache = new Map<string, string>()

  private getMetaContent(name: string): string | null {
    if (this.cache.has(name)) {
      return this.cache.get(name) || null
    }

    const meta = document.querySelector(`meta[name="${name}"]`)
    const content = meta?.getAttribute('content') || null

    // Check if this looks like an unreplaced Django template tag
    if (content && content.includes('{{') && content.includes('}}')) {
      console.warn(`[ServerVariables] Found unreplaced template tag: ${content} for ${name}`)
      this.cache.set(name, '')
      return null
    }

    this.cache.set(name, content || '')

    return content
  }

  /**
   * Parses regionId and dealId from the URL.
   */
  private getFromUrl(): { regionId: number | null; dealId: number | null } {
    // Try path pattern first (staging/prod via Django)
    // Matches both /apps/deal/{dealId}/{regionId} and /apps/dealroom/{dealId}/{regionId}
    const pathMatch = window.location.pathname.match(/\/apps\/(?:deal|dealroom)\/(\d+)\/(\d+)/)
    if (pathMatch) {
      return { regionId: Number(pathMatch[1]), dealId: Number(pathMatch[2]) }
    }

    // Try hash query params (preview deployments served statically from S3)
    // URL format: /react/pr-*/dealroom/index.html#/route?dealId=X&regionId=Y
    const hash = window.location.hash
    const hashQueryIndex = hash.indexOf('?')
    if (hashQueryIndex !== -1) {
      const hashParams = new URLSearchParams(hash.slice(hashQueryIndex + 1))
      const dealId = hashParams.get('dealId')
      const regionId = hashParams.get('regionId')
      if (dealId && regionId) {
        return { regionId: Number(regionId), dealId: Number(dealId) }
      }
    }

    // Try page query params so deal context survives hash-router navigation
    // URL format: /react/pr-*/dealroom/index.html?regionId=X&dealId=Y#/route
    const pageParams = new URLSearchParams(window.location.search)
    const dealId = pageParams.get('dealId')
    const regionId = pageParams.get('regionId')
    if (dealId && regionId) {
      return { regionId: Number(regionId), dealId: Number(dealId) }
    }

    return { regionId: null, dealId: null }
  }

  get regionHost(): string {
    return this.getMetaContent('cf-region-host') || ''
  }

  get regionId(): number | null {
    const value = this.getMetaContent('cf-region-id')
    if (value) return Number(value)
    return this.getFromUrl().regionId
  }

  get dealId(): number | null {
    const value = this.getMetaContent('cf-deal-id')
    if (value) return Number(value)
    return this.getFromUrl().dealId
  }

  get isAvailable(): boolean {
    const hasValues = !!this.regionHost || !!this.regionId || !!this.dealId
    if (!hasValues) {
      console.log(
        '[ServerVariables] No server variables available, will fallback to token-based approach',
      )
    }
    return hasValues
  }

  getAll(): Record<string, string | number | null> {
    return {
      regionHost: this.regionHost,
      regionId: this.regionId,
      dealId: this.dealId,
    }
  }

  clearCache(): void {
    this.cache.clear()
  }
}

export const serverVariables = new ServerVariablesService()
